/**
 * Agent Chat 상수·순수 헬퍼 (패널 전용).
 *
 * ⚠️ 이 파일은 pages/** 를 import 하지 않는다. 여기 남은 값은 "챗 자신이 아는 것"뿐이다 —
 *   백엔드 라우팅 능력(SUPPORTED_CHAT_MODULES), 도구 라벨 규약(CONSULTED_*), 칩 정규화.
 *   화면 정책(단계 이름·시나리오 상한·슬래시 카테고리 등)은 어댑터로 주입받는다.
 */
import EditNoteIcon from "@mui/icons-material/EditNote";
import LayersIcon from "@mui/icons-material/Layers";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BoltIcon from "@mui/icons-material/Bolt";

import { resolveTraceArgs, keyedText } from "@plannel/components/ai/aiTraceArgs";

// 슬래시 팔레트 카테고리 아이콘 — 어댑터는 아이콘을 **이름 문자열**로 넘긴다.
//   (화면→챗으로 React 컴포넌트가 흐르면 정책 채널이 UI 채널을 겸하게 된다.)
//   미지의 이름은 BoltIcon 으로 폴백 — 카테고리가 사라지는 것보다 낫다.
export const SLASH_ICONS = {
  edit: EditNoteIcon,
  layers: LayersIcon,
  add: AddCircleOutlineIcon,
  close: CloseIcon,
  ai: AutoAwesomeIcon,
  check: CheckCircleIcon,
  forward: ArrowForwardIcon,
  bolt: BoltIcon,
};
export const slashIcon = (name) => SLASH_ICONS[name] || BoltIcon;

// 채팅 백엔드(ScenarioAiV1Controller#dispatch)가 실제 라우팅하는 모듈코드 — 그 외(navbar 전역 챗봇 등)는
// 네트워크 호출 없이 미지원 안내로 처리(불필요한 stubResponse 왕복 방지).
//   GENERAL = 시나리오 플래닝 밖 화면(홈·업무 화면)의 전역 챗 라우트. 모듈 컨텍스트가 없는
//   질의를 그대로 "모듈 없음"으로 보낸다 — 예전처럼 마지막 모듈이나 DP 로 위조하지 않는다.
export const SUPPORTED_CHAT_MODULES = ["BF", "DP", "IP", "RP", "MP", "FP", "GENERAL"];

/**
 * 챗 세션 id — 서버 저장 형태(uuid4.hex)와 같은 32자 hex 불투명 문자열.
 *
 * 주의: 세그먼트에 의미를 담지 않는다. 예전 형식(`dp-chat-{userId}-{modCode}-{ts}`)은 세 조각이
 *   모두 사실과 달랐다(dp 는 모듈 무관 리터럴, userId 는 항상 anonymous, modCode 는 앱 최초
 *   렌더값으로 고정). 게다가 사용자 구분자가 없어 같은 밀리초에 로드한 두 사용자의 id 가
 *   충돌하면 대화가 한 세션으로 합쳐졌다. 모듈은 DB 의 module_cd 컬럼이 정본이다.
 * 주의: crypto.randomUUID 는 보안 컨텍스트(https/localhost)에서만 존재한다 — http 로 뜬
 *   환경에서는 undefined 라 아래 폴백이 반드시 필요하다.
 */
export const newChatSessionId = () => {
  try {
    if (typeof crypto !== "undefined") {
      if (typeof crypto.randomUUID === "function") return crypto.randomUUID().replace(/-/g, "");
      if (typeof crypto.getRandomValues === "function") {
        const b = new Uint8Array(16);
        crypto.getRandomValues(b);
        return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
      }
    }
  } catch {
    /* 아래 폴백 */
  }
  let s = "";
  while (s.length < 32) s += Math.floor(Math.random() * 0x10000).toString(16).padStart(4, "0");
  return s.slice(0, 32);
};

// (단계 이름은 화면이 어댑터로 준다 — agentChatAdapter.steps.nameKeys.
//  예전엔 여기 STEP_NAME_KEYS 사본이 있었고 화면의 STEP_LABELS_CREATE 와 마지막 항목이 어긋나 있었다.)

// '참고한 데이터' 칩 라벨 — 도구 진행 라벨 키는 진행형 문구("위험·문제 품목을 찾고 있습니다")라
// 칩에 그대로 쓰면 어색하다. 같은 키의 명사형(`${label}Noun`, 예: "위험·문제 품목")을 우선 쓰고,
// 명사형 키가 없는 라벨(RP suggest 계열 trace* 등)은 원문으로 폴백한다.
export function consultedChipLabel(t, chip) {
  const args = resolveTraceArgs(t, chip.labelArgs) || {};
  return t(`${chip.label}Noun`, { ...args, defaultValue: t(chip.label, args) });
}

// '참고한 데이터' 칩은 답변 수치의 **원천 데이터**만 보여줘야 한다 — 사용자가 근거를 확인하는 용도다.
// 값을 조회하지 않는 보조 도구(정의 조회·차트 렌더·이름→코드 해석·문서 검색·초안 생성 등)는
// 작업일 뿐 데이터 근거가 아니므로 칩에서 제외한다(라벨 키 단위 — 같은 성격 도구가 함께 걸린다).
export const CONSULTED_EXCLUDE_LABELS = new Set([
  "chatToolGlossary",     // 지표 정의 조회 (값 아님)
  "chatToolChart",        // 차트 렌더 (이미 조회한 값의 재표현)
  "chatToolTree",         // 계층 표 렌더 (이미 조회한 값의 재표현)
  "chatToolFlow",         // 흐름도 렌더 (이미 조회한 흐름의 재표현)
  "chatToolResolve",      // 이름→코드 대상 판별
  "chatToolWiki",         // 위키 문서 검색
  "chatToolDraft",        // 시나리오 초안 생성
  "chatToolSettings",     // 설정값 조회 (데이터 근거 아님)
  "chatToolGeneric",      // 미분류 도구 — 무엇을 봤는지 정보가 없어 근거로 부적합
]);

// 복원 말풍선 본문 재조립 — 라이브에서 "…(으)로 변경했습니다" 같은 **실행 확인 문구는 프론트가**
//   i18n 으로 조립하고(패널의 confirms), 서버 payload.reply 는 빈 문자열이라 저장 시 키째 빠진다.
//   그래서 복원하면 본문 없는 말풍선(패딩·테두리만 있는 회색 상자)이 남았다.
//   저장된 actions 로 같은 키를 다시 써서 문구를 만든다 — 표시 언어도 현재 설정을 따라간다.
//   ⚠️ 라이브 문구와 **완전히 같지는 않다**: 대상 시나리오(role)가 비어 있던 턴은 라이브에서
//   되묻기/지연적용 안내였을 수 있으므로, 완료를 단정하지 않고 액션 라벨을 그대로 기록한다.
export function restoredActionText(t, actions, stepNameKeys = []) {
  const lines = [];
  // 저장된 액션 라벨도 label_key(+label_args) 우선 — 복원 말풍선이 현재 언어로 읽힌다.
  const actLbl = (a) => keyedText(t, a.label_key, a.label, a.label_args);
  for (const a of actions || []) {
    if (!a || typeof a !== "object") continue;
    const p = a.payload || {};
    if (a.action === "set_param") {
      if (!p.role) lines.push(actLbl(a));               // 대상 미지정 — 완료 단정 금지
      else if (p.displayValue) lines.push(t("MSG_ChangedToValue", { value: p.displayValue }));
      else lines.push(actLbl(a));
    } else if (a.action === "run_engine") {
      lines.push(t("MSG_RunningEngine"));
    } else if (a.action === "apply_expert_case") {
      lines.push(t("MSG_ComposedFromNamedCase", { name: p.scenario_name || t("caseDefaultLabel") }));
    } else if (a.action === "select_mode") {
      lines.push(t("MSG_ComposedByMethod", { method: p.displayValue || t("selectedDefaultLabel") }));
    } else if (a.action === "navigate") {
      const st = p.step;
      lines.push(typeof st === "number" && stepNameKeys[st]
        ? t("agentNavMovedToStep", { step: t(stepNameKeys[st]) }) : t("agentNavMovedGeneric"));
    } else if (a.action === "nav_screen") {
      // 화면 이동은 클릭을 받아야 실행되므로 완료를 단정하지 않고 제안 라벨을 남긴다.
      lines.push(p.name ? t("agentNavMoveToScreenLabel", { screen: p.name }) : actLbl(a));
    } else if (a.label || a.label_key) {
      lines.push(actLbl(a));
    }
  }
  return lines.filter(Boolean).join("\n");
}

// 서버 세션 → 화면 메시지. 본문이 빈 어시스턴트 턴만 actions 로 보강하고, 그래도 비면 버린다
//   (내용 없는 말풍선을 이력에 남기지 않는다). 나머지 필드는 서버가 준 그대로 통과시킨다.
//   resolveLinks: 서버가 준 screen_links 를 칩 데이터로 바꾸는 변환(존재·권한 확인 포함).
//   서버 항목은 표시명을 name 에 담는데 칩은 title 을 읽는다 — 그대로 통과시키면 라벨이 빈다.
export function hydrateRestoredMessages(t, msgs, stepNameKeys = [], resolveLinks = null) {
  return (msgs || []).reduce((acc, m) => {
    if (!m || m.role !== "a") { acc.push(m); return acc; }
    let msg = m;
    if (resolveLinks && msg.screenLinks) {
      const links = resolveLinks(msg.screenLinks);
      msg = { ...msg };
      // 권한이 사라진 화면뿐이면 필드째 떼어낸다 — 빈 배열을 남길 이유가 없다.
      if (links.length) msg.screenLinks = links;
      else delete msg.screenLinks;
    }
    if (msg.text) { acc.push(msg); return acc; }
    const text = restoredActionText(t, msg.actions, stepNameKeys);
    if (text) acc.push({ ...msg, text });
    // 본문 없이 링크만 남은 턴 — 라이브 경로와 같은 안내 한 줄로 칩을 얹을 자리를 만든다.
    else if (msg.screenLinks?.length) acc.push({ ...msg, text: t("agentLinkOnlyBody") });
    else if (msg.expertCases || msg.customDraft) acc.push(msg);
    return acc;
  }, []);
}

// Quick Chip 정규화 — 화면이 주는 quickPrompts 항목을 두 형태 모두 지원:
//   · 문자열                → 단순 질문/프롬프트 (액션 없음)
//   · { i18nKey, ko, intent? } → 다국어 라벨 + 구조화 액션 의도(디스크립터)
// 라벨은 t(i18nKey, {defaultValue: ko}) 로 해석 → JSON 미등록 상태에서도 ko 로 표시되고,
// l10n 파일에 키를 채우면 자동으로 다국어 전환된다.
//   intent : 클릭 시 백엔드로 함께 보내는 action_hint (LLM 미경유 결정적 실행)
export function resolveChip(chip, t) {
  if (chip == null) return null;
  if (typeof chip === "string") return { label: chip, sendText: chip, intent: null };
  const label = chip.i18nKey ? t(chip.i18nKey, { defaultValue: chip.ko ?? "" }) : (chip.ko ?? "");
  return { label, sendText: label, intent: chip.intent ?? null };
}

/*
  서버가 주는 챗 타임스탬프는 **타임존 표기가 없는 UTC** 문자열이다.
    z_ai_chat_* 의 last_ts/created_ts 는 TIMESTAMP(naive) + DEFAULT CURRENT_TIMESTAMP 이고
    (docs/sql/z_ai_chat_ddl.sql), DB 세션 TZ 가 UTC 라 저장값이 UTC 벽시계다.
    파이썬은 그걸 datetime.isoformat() 으로 그대로 내보낸다 → "2026-08-11T02:00:00".
  그런데 JS 는 **오프셋 없는 date-time 을 로컬 시각으로** 파싱한다(ES 규격). KST 에서는
    방금 보낸 메시지가 9시간 전으로 보인다.

  ⚠️ 오프셋이 **없을 때만** UTC 로 못박는다 — 서버가 나중에 오프셋을 붙이면(AT TIME ZONE 또는
     TIMESTAMPTZ 전환) 이 분기는 조건에 안 걸려 자동으로 죽는다. 날짜만 있는 값도 건드리지
     않는다(ES 규격상 date-only 는 이미 UTC 로 해석된다).
*/
const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/;
export function parseServerTs(ts) {
  if (typeof ts === "string" && /\d{2}:\d{2}/.test(ts) && !HAS_OFFSET.test(ts)) return new Date(`${ts}Z`);
  return new Date(ts);
}

// 세션 목록의 상대 시각 — 목록 전용 간이 포맷(분/시간/그 외 월·일).
//   패널 헤더의 기록 Popover 와 홈 카드의 '최근 대화' 목록이 같은 표기를 쓰도록 공유한다.
export function relTime(t, ts) {
  try {
    const d = parseServerTs(ts);
    const m = Math.floor((Date.now() - d.getTime()) / 60000);
    if (m < 1) return t("agentChatJustNow");
    if (m < 60) return t("agentChatMinAgo", { n: m });
    const h = Math.floor(m / 60);
    if (h < 24) return t("agentChatHourAgo", { n: h });
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } catch { return ""; }
}

// 되묻기 '값 입력' 단계의 검증 — 범위를 벗어나거나 숫자가 아니면 확정 값 없음(null).
//   확정 여부(버튼 활성)는 선택지 패널이, 확정 후 처리는 엔진이 판단하므로 순수 함수로 공유한다.
export function choiceInputValueOf(inp, raw0) {
  const raw = String(raw0 ?? "").trim();
  if (!inp || !raw) return null;
  // 자유 텍스트(서술형 답변) — 파라미터 값이 아니라 다음 발화로 보낼 문장.
  //   너무 짧으면 후속 처리(도구 호출)가 불가능하므로 min_len 을 만족할 때만 확정한다.
  if (inp.kind === "text") {
    if (inp.min_len && raw.length < inp.min_len) return null;
    return { value: raw, display: raw };
  }
  if (inp.type === "number" || inp.type === "percent") {
    const n = Number(raw.replace("%", "").trim());
    if (!Number.isFinite(n)) return null;
    if (inp.min != null && n < inp.min) return null;
    if (inp.max != null && n > inp.max) return null;
    if (inp.type !== "percent") return { value: String(n), display: String(n) };
    // store="ratio" — 저장 정본이 소수/배수인 파라미터(MP 수율 0.95 · 효율 1.2). 표시만 %.
    //   이 입력 경로는 백엔드 _coerce 를 타지 않으므로 같은 환산을 여기서 해야 한다.
    if (inp.store === "ratio") {
      const p = Math.round(n * 10) / 10;          // 소수 첫째자리 유지(97.5% 를 98% 로 만들지 않는다)
      return { value: Number((p / 100).toFixed(4)), display: `${p}%` };
    }
    return { value: `${Math.round(n)}%`, display: `${Math.round(n)}%` };
  }
  return { value: raw, display: raw };
}

// 지연 적용(보관된 파라미터 변경)의 주인 — 보관한 화면·모듈. 엔진은 앱 셸에 하나라 보관값이
//   화면 전환을 넘어 살아남는다. 주인이 아닌 화면이 소비하면 BF 에서 받은 「외부 인자」 가
//   FP 카드에 「변경했습니다」 로 떨어진다(값은 어디에도 안 들어간다). 보관·소비 양쪽이 이 키로
//   대조한다 — 문자열이라 재게시(publish)마다 바뀌는 screen 객체와 무관하다. [Q-131]
export const pendingOwnerKey = (screen) =>
  `${screen?.scopeId ?? "__global__"}|${screen?.modCode ?? ""}`;

// 이 보관값을 지금 화면이 소비해도 되는가. 주인이 다르면 소비하지 않는다(보관은 유지 —
//   주인 화면으로 돌아가 시나리오를 만들면 그때 적용된다).
export const isPendingOwner = (pending, ownerKey) => !!pending && pending.owner === ownerKey;

// 응답 대기 중 진행 상태 (의사 단계 — 실제 tool 진행 표시는 SSE 스트리밍이 덮어쓴다)
// 값은 i18n 키 문자열 — 모듈 레벨 상수라 t()를 직접 쓸 수 없으므로 소비처에서 t(key)로 변환한다.
export const STATUS_STEPS = {
  action: ["statusRequestInterpreting", "statusTaskPreparing"],
};
